import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { createBrowserHistory } from "history";
import React, { useEffect } from "react";
import { connect, ConnectedProps } from "react-redux";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  useLocation
} from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import styled from "styled-components";
import MobileMenu from "sweet-web/components/TopBar/MobileMenu";
import ErrorBoundary from "sweet-web/components/universal/ErrorBoundary";
import { STRIPE_API_KEY } from "sweet-web/keys";
import { RootState } from "sweet-web/redux";
import { Sync } from "sweet-web/redux/app/actions";
import * as selectors from "sweet-web/redux/selectors";
import AccountManager from "./Account/Manager";
import AccountUser from "./Account/User";
import MakePayment from "./Booking/MakePayment";
import Dashboard from "./Dashboard";
import Discover from "./Discover";
import Footer from "./Footer";
import Inbox from "./Inbox";
import JoinUser from "./Join/User";
import JoinVenue from "./Join/Venue";
import Privacy from "./Legal/Privacy";
import Terms from "./Legal/Terms";
import ConstructListing from "./Listing/ConstructListing";
import LinkStripe from "./Listing/LinkStripe";
import MyListings from "./Listing/MyListings";
import VenuePreview from "./Listing/Preview";
import Login from "./Login";
import VenuePhotos from "./PhotoBox/VenuePhotos";
import Reset from "./Reset";
import TopBar from "./TopBar";
import ScrollToTop from "./universal/ScrollToTop";
import MobileBook from "./Venue/MobileBook";
import ViewVenue from "./Venue/ViewVenue";

const stripePromise = loadStripe(STRIPE_API_KEY);

const Wrapper = styled.div`
  width: 100%;
  min-height: 100%;
  overflow: auto;
  padding-top: 60px;
  box-sizing: border-box;
`;

const Content = styled.div`
  width: 100%;
  min-height: ${window.innerHeight - 60}px;
  overflow: auto;
  padding: 0 60px;
  box-sizing: border-box;
  @media screen and (max-width: 600px) {
    padding: 0 20px;
  }
`;

type Props = ConnectedProps<typeof connector>;

const App: React.FC<Props> = props => {
  const isVenue = props.userType === "VENUE" || props.userType === "ADMIN";

  useEffect(() => {
    props.Sync();
  }, []);

  return (
    <Elements stripe={stripePromise}>
      <Wrapper>
        <Router>
          <ErrorBoundary>
            <TopBar />
            <Content id="sweet-web-content">
              <Switch>
                <Route
                  path="/"
                  component={props.loggedIn && isVenue ? Dashboard : Discover}
                  exact={true}
                />
                <Route
                  path={"/v/:venueId"}
                  component={ViewVenue}
                  exact={true}
                />
                <Route
                  path={"/v/:venueId/book"}
                  component={MobileBook}
                  exact={true}
                />
                <Route
                  path="/v/:venueId/photos"
                  component={VenuePhotos}
                  exact={true}
                />
                {props.loggedIn && isVenue && (
                  <Route
                    path="/listing/:venueId/preview"
                    exact={true}
                    component={VenuePreview}
                  />
                )}
                {props.loggedIn && isVenue && (
                  <Route
                    path="/listing/:venueId/preview/photos"
                    exact={true}
                    component={VenuePhotos}
                  />
                )}
                {props.loggedIn && (
                  <Route
                    path={["/c", "/c/:chatId"]}
                    component={Inbox}
                    exact={true}
                  />
                )}
                {props.loggedIn && (
                  <Route
                    path={"/b/:bookingId/pay"}
                    component={MakePayment}
                    exact={true}
                  />
                )}
                {props.loggedIn && (
                  <Route
                    path="/me"
                    component={isVenue ? AccountManager : AccountUser}
                    exact={true}
                  />
                )}
                {props.loggedIn && isVenue && (
                  <Route path="/listings" component={MyListings} exact={true} />
                )}
                {props.loggedIn && isVenue && (
                  <Route
                    path={[
                      "/listing/:venueId",
                      "/listing/:venueId/:pageId",
                      "/listing/:venueId/:pageId/:subPageId"
                    ]}
                    component={ConstructListing}
                    exact={true}
                  />
                )}
                {props.loggedIn && isVenue && (
                  <Route path={"/stripe"} component={LinkStripe} exact={true} />
                )}
                {!props.loggedIn && (
                  <Route path="/list" component={JoinVenue} exact={true} />
                )}
                {!props.loggedIn && (
                  <Route path="/join" component={JoinUser} exact={true} />
                )}
                {!props.loggedIn && (
                  <Route path="/login" component={Login} exact={true} />
                )}
                <Route path="/reset" component={Reset} exact={true} />
                <Route path="/terms" component={Terms} exact={true} />
                <Route path="/privacy" component={Privacy} exact={true} />
                <Redirect to="/" />
              </Switch>
            </Content>
            <Footer />
            <ScrollToTop />
          </ErrorBoundary>
        </Router>
      </Wrapper>
    </Elements>
  );
};

const mapState = (state: RootState) => ({
  loggedIn: selectors.loggedIn(state),
  userType: selectors.userType(state)
});
const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      Sync
    },
    dispatch
  );
const connector = connect(mapState, mapDispatch);

export default connector(App);
